// Places and gaps. During the race they come from signalPos, and finished cats keep their crossing order.
// The podium uses only the official times from the finished frame; DSQ comes only from that frame.
import { CAT_IDS, type CatId, type RaceSnapshot, type SignalFrame } from './types';

export interface StandingInput { lane: number; signalPos: number; finishPlace?: number }
export interface Standing { place: number; gapToLeader: number }

/** Places 1..n and gaps (share of the track) for cats in lane order. */
export function standings(cats: readonly StandingInput[]): Standing[] {
  const order = [...cats].sort((a, b) => {
    const fa = a.finishPlace ?? Infinity, fb = b.finishPlace ?? Infinity;
    if (fa !== fb) return fa - fb;
    if (b.signalPos !== a.signalPos) return b.signalPos - a.signalPos;
    return a.lane - b.lane;
  });
  const lead = order[0]?.signalPos ?? 0;
  const out: Standing[] = cats.map(() => ({ place: 0, gapToLeader: 0 }));
  order.forEach((c, i) => {
    const k = cats.indexOf(c);
    out[k] = { place: i + 1, gapToLeader: Math.max(0, lead - c.signalPos) };
  });
  return out;
}

/** Official results from a finished frame: fastest first, disqualified cats after everyone else. */
export function officialResults(times: NonNullable<SignalFrame['times']>): NonNullable<RaceSnapshot['results']> {
  const rows = times.slice(0, CAT_IDS.length).map((t, lane) => ({ id: CAT_IDS[lane] as CatId, lane, seconds: t.seconds, dsq: t.dsq }));
  const order = [...rows].sort((a, b) => Number(a.dsq) - Number(b.dsq) || a.seconds - b.seconds || a.lane - b.lane);
  return rows.map((r) => ({ id: r.id, seconds: r.seconds, dsq: r.dsq, place: order.indexOf(r) + 1 }));
}
